import { StockItem } from '../types';

export const getDisplayName = (item: StockItem, filteredData: StockItem[], stockData: StockItem[]): string => {
  if (item.type === 'variation' && item.parent_product_id) {
    const parent = stockData.find((parentItem) => parentItem.id === item.parent_product_id);
    if (parent) {
      const otherVariations = filteredData.filter(
        (filteredItem) =>
          filteredItem.parent_product_id === item.parent_product_id &&
          filteredItem.id !== item.id &&
          filteredItem.type === 'variation'
      );
      if (otherVariations.length > 0) {
        return `${parent.name} (${item.name})`;
      }
      return parent.name;
    }
  }
  return item.name;
};

const MAX_STOCK_HEALTH = 500;

// Postgres NUMERIC columns (avg_sales_per_month, target_stock_days) come back from the API
// as strings, so these are coerced explicitly rather than compared/divided directly.
const toNumber = (value: unknown): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

export const calculateStockDays = (item: StockItem): string => {
  const avgSales = toNumber(item.avg_sales_per_month);
  if (item.stock_quantity === null || avgSales === 0) {
    return 'N/A';
  }
  const stockDays = (toNumber(item.stock_quantity) / avgSales) * 30;
  return stockDays.toFixed(0);
};

/** No sellable stock on hand. Managed items only — a null stock_quantity is unmanaged, not empty. */
export const isOutOfStock = (item: StockItem): boolean =>
  item.stock_quantity !== null && toNumber(item.stock_quantity) <= 0;

export const computeStockHealth = (item: StockItem): number | null => {
  const targetDays = toNumber(item.target_stock_days);
  if (item.stock_quantity === null || targetDays === 0) {
    return null;
  }
  const stockQuantity = toNumber(item.stock_quantity);
  const avgSales = toNumber(item.avg_sales_per_month);
  if (avgSales === 0) {
    return stockQuantity > 0 ? 100 : 0;
  }
  const stockDays = (stockQuantity / avgSales) * 30;
  const stockHealth = (stockDays / targetDays) * 100;
  return isFinite(stockHealth) ? stockHealth : null;
};

export const calculateStockHealth = (item: StockItem): string => {
  // Zero or negative stock is always 0% — label it in words rather than as a
  // percentage, so an empty shelf reads as such at a glance.
  if (isOutOfStock(item)) {
    return 'Out Of Stock';
  }
  const stockHealth = computeStockHealth(item);
  if (stockHealth === null) {
    return 'N/A';
  }
  if (stockHealth > MAX_STOCK_HEALTH) {
    return `> ${MAX_STOCK_HEALTH}%`;
  }
  // Stock on hand but selling fast enough to round to 0% — keep it distinct
  // from genuinely out of stock.
  if (stockHealth < 1) {
    return '< 1%';
  }
  return `${stockHealth.toFixed(0)}%`;
};

/**
 * Stock Health column ordering, direction included.
 * Out-of-stock items (health 0) group ahead of everything with stock and rank
 * among themselves by monthly sales, highest first — with nothing left to sell,
 * days-of-cover says nothing, but what we sell the most of is what hurts most.
 * N/A rows stay last in either direction, as they do for every other column.
 */
export const compareStockHealth = (a: StockItem, b: StockItem, order: 'asc' | 'desc'): number => {
  const aOut = isOutOfStock(a);
  const bOut = isOutOfStock(b);
  const aHealth = aOut ? 0 : computeStockHealth(a);
  const bHealth = bOut ? 0 : computeStockHealth(b);

  if (aHealth === null && bHealth === null) return 0;
  if (aHealth === null) return 1;
  if (bHealth === null) return -1;

  const cmp =
    aOut && bOut
      ? toNumber(b.avg_sales_per_month) - toNumber(a.avg_sales_per_month)
      : aHealth - bHealth;
  return order === 'desc' ? -cmp : cmp;
};

export const getStockHealthClass = (item: StockItem): string => {
  const stockHealth = computeStockHealth(item);
  if (stockHealth === null) {
    return 'stock-health-na';
  }
  if (stockHealth < 20) {
    return 'stock-health-low';
  }
  if (stockHealth < 80) {
    return 'stock-health-medium';
  }
  return 'stock-health-high';
};

export const validateChanges = (changes: { [key: string]: any }, item: StockItem): boolean => {
  // Collect all stock values to validate (from warehouse_stocks)
  const stockValues = [
    ...(changes.warehouse_stocks
      ? Object.values(changes.warehouse_stocks)
      : Object.values(item.warehouse_stocks)),
  ];

  // Add avg_sales_per_month and target_stock_days
  stockValues.push(
    changes.avg_sales_per_month !== undefined ? changes.avg_sales_per_month : item.avg_sales_per_month,
    changes.target_stock_days !== undefined ? changes.target_stock_days : item.target_stock_days
  );

  for (const fieldValue of stockValues) {
    // Handle both number and string (due to input fields)
    const numericValue = Number(fieldValue);
    if (isNaN(numericValue) || numericValue < -50) {
      alert('Stock fields cannot be less than -50.');
      return false;
    }
    if (fieldValue === '' || fieldValue === null) {
      alert('Stock fields cannot be blank.');
      return false;
    }
  }
  return true;
};